import { type ColumnFormatInfo } from '@blaze-shared/api-contracts-domain';
import { isDatasetLinkValue, type WorkflowSchema } from '@blaze-shared/schema-domain';
import { formatDuration } from '@blaze-shared/shared-feature';
import { roundToPrecision } from '@blaze-shared/shared-util';

const NEW_LINE = '\r\n';

export interface CsvFileContentRequest {
  /** Whether the author picked the columns themselves, which fixes both the set and their order. */
  chooseColumns: boolean;
  /** The columns the node declares, by id or by name. */
  columns: string[];
  data: any[];
  /** Resolves a column id to its name; ids are what nodes store, names are what rows are keyed by. */
  resolveColumnName: (columnIdOrName: string) => string;
  /** Orders the columns and formats their values when known. */
  schema?: WorkflowSchema;
}

/**
 * Renders rows as the lines of a CSV file, header first.
 *
 * @returns one string per line, each already newline-terminated.
 */
export function buildCsvFileContent({
  chooseColumns,
  columns,
  data,
  resolveColumnName,
  schema,
}: CsvFileContentRequest): string[] {
  const columnNames = collectColumnNames({
    chooseColumns,
    columns,
    data,
    resolveColumnName,
    schema,
  });
  const includedColumns = new Set(columnNames);
  const headerAndRows = [...columnNames, NEW_LINE];

  return [
    joinCsvLine(headerAndRows),
    ...data.map((row) => joinCsvLine(toCsvCells(row, headerAndRows, includedColumns, schema))),
  ];
}

/**
 * Formats a value the way the column declares it — a duration as its configured duration format, a
 * ratio as a percentage. Anything else is written as it is.
 */
function formatCsvValue(value: number | string, format?: ColumnFormatInfo): number | string {
  if (!format) {
    return value;
  }
  if (typeof value === 'string' && format.formatType === 'Duration') {
    return formatDuration(value, format.durationFormat);
  }
  if (typeof value === 'number' && format.formatType === 'Percent') {
    return `${roundToPrecision(value * 100, 2)}%`;
  }
  return value;
}

/**
 * Decides which columns the file has, and in which order.
 *
 * The node's own column list is authoritative when the author chose it. Otherwise the columns are
 * read off the data as well, so that a node saved before the list existed still exports
 * everything, and the schema's order wins over the order the keys happen to appear in.
 */
function collectColumnNames({
  chooseColumns,
  columns,
  data,
  resolveColumnName,
  schema,
}: CsvFileContentRequest): string[] {
  let columnNames = columns.map((columnIdOrName) => resolveColumnName(columnIdOrName) || '');

  if (!columnNames.length || (!chooseColumns && data?.length)) {
    data.forEach((row) => {
      columnNames = columnNames.concat(
        Object.keys(row).map((columnIdOrName) => resolveColumnName(columnIdOrName) || ''),
      );
    });
    if (schema?.columns?.length) {
      columnNames = sortBySchemaOrder(Array.from(new Set(columnNames)), schema);
    }
  }

  const unique = Array.from(new Set(columnNames));
  return schema?.columns?.length && !chooseColumns ? sortBySchemaOrder(unique, schema) : unique;
}

/** Orders columns as the schema declares them; columns the schema does not know go last. */
function sortBySchemaOrder(columnNames: string[], schema: WorkflowSchema): string[] {
  const schemaIndex = (columnName: string): number =>
    schema.columns.findIndex((col) => col.name === columnName);
  return [...columnNames].sort((colNameA, colNameB) => {
    const indexA = schemaIndex(colNameA);
    const indexB = schemaIndex(colNameB);
    if (indexA === indexB) {
      return 0;
    }
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }
    return indexA < indexB ? -1 : 1;
  });
}

/**
 * Places a row's values in column order, quoting whatever CSV would otherwise misread.
 *
 * A linked value is written as what it displays rather than as its raw link; anything structured
 * is written as JSON.
 */
function toCsvCells(
  row: any,
  columnNames: string[],
  includedColumns: Set<string>,
  schema?: WorkflowSchema,
): any[] {
  const cells: any[] = [];
  Object.entries(row).forEach(([colName, value]) => {
    const isIncluded = includedColumns.size ? includedColumns.has(colName) : true;
    if (!isIncluded) {
      return;
    }
    const colIndex = columnNames.indexOf(colName);
    if (value && (Array.isArray(value) || typeof value === 'object')) {
      cells[colIndex] = isDatasetLinkValue(value)
        ? value['display'] || value['rowId']
        : JSON.stringify(value);
    } else {
      const valueFormat = schema?.columns.find((col) => col.name === colName)?.format;
      cells[colIndex] =
        (typeof value === 'number' || typeof value === 'string') && valueFormat?.formatType
          ? formatCsvValue(value, valueFormat)
          : value;
    }
  });
  cells[columnNames.length - 1] = NEW_LINE;

  return cells.map(quoteWhenAmbiguous);
}

/** Quotes a value CSV would otherwise split or mis-parse, doubling any quotes inside it. */
function quoteWhenAmbiguous(value: any): any {
  if (!value || typeof value !== 'string') {
    return value;
  }
  let isArray = false;
  let isObject = false;
  let isStringWithCommas = value.includes(',');
  try {
    const parsed = JSON.parse(value);
    isArray = Array.isArray(parsed);
    isObject = !isArray && typeof parsed === 'object' && !!Object.keys(parsed)?.length;
    isStringWithCommas = isStringWithCommas && !isArray && !isObject;
  } catch {
    // not JSON — only the comma check applies
  }
  const escaped = value.replaceAll('"', '""');
  return isArray || isObject || isStringWithCommas ? `"${escaped}"` : escaped;
}

/** Joins cells with commas, without leaving a comma in front of the line break that ends the row. */
function joinCsvLine(cells: any[]): string {
  return cells.join(',').replace(`,${NEW_LINE}`, NEW_LINE);
}
